// @flow

import * as React from "react";
import { ReactForm, ReactFormField } from "@adrenalin/react-form";
import RadioGroup from "../components/RadioGroup";
import FileUploadField from "../components/FileUploadField";
import ServerError from "../components/ServerError";
import DatePickerField from "../components/DatePickerField";
import RenderHTML from "../components/RenderHTML";
import CustomField from "../components/CustomField";
import CustomSelectField from "../components/CustomSelectField";
import { scrollToEl } from "../modules/smooth-scroll";
import { drupalDate } from "../constants/Validators";
import useMergableState from "../hooks/useMergableState";

import useDatePickerState from "../hooks/useDatePickerState";

const { useRef, useState } = React;

type PropsType = {
    elements: Array<any>,
    form_build_id: string,
    form_id: string,
    form_token: string,
    captcha: string,
    submit: { name: string },
    errors: Object,
};

const ReactFormLoader = (props: PropsType): React.Node => {
    // Refs
    const topOfFormRef = useRef(); // Used for scrolling to the top of the form on error
    const fileUploaderRef = useRef(); // Used for manual validation of the file upload field
    const recaptchaContainer = useRef(); // Used for manual validation of recaptcha field

    // State vars
    const [formErrors, setFormErrors] = useState(
        ([]: Array<{ error: string, label: string }>)
    );
    const [inputValues, setInputValues] = useMergableState(
        props.elements.reduce(
            (prev: any, el: any): any => ({
                ...prev,
                [el.name]: el.value,
            }),
            {}
        )
    );
    const { datePickersState, setDateFieldValue } = useDatePickerState(
        props.elements
            .filter((el: any): boolean => el.type === "date")
            .reduce(
                (prev: any, el: any): any => ({
                    ...prev,
                    [`datepicker-${el.name}`]: "",
                }),
                {}
            )
    );

    // Custom validators to pass the root ReactForm Element
    const customValidators = [drupalDate];

    // Maps field values from what Drupal passes us to what the react-form
    // library expects for each field
    const mapTwigToReactField = (input: any): any => {
        const typeMapping = {
            textfield: "text",
            tel: "tel",
            email: "email",
            date: "text",
            textarea: "textarea",
            select: "select",
            managed_file: "file",
        };

        const type = typeMapping[input.type];

        const mappedFields: any = {
            type,
            label: input.title,
            name: input.name,
            placeholder: input.placeholder || "",
            required: input.required || false,
            errorMessages: {
                required: input.required_error,
            },
            value: input.value,
            validators: [],
            maxLength: input.maxlength || null,
        };

        // Add validators
        if (input.type === "tel") {
            mappedFields.validators.push("mobile");
        }
        if (input.type === "email") {
            mappedFields.validators.push("email");
        }
        if (input.type === "date") {
            mappedFields.validators.push("drupal-date");
        }

        // Extra file type field
        if (input.type === "managed_file") {
            mappedFields.fileValidations = {
                maxSizePretty: input.max_filesize,
                maxSizeBytes: input.upload_validators.file_validate_size,
                allowedExtensions:
                    input.upload_validators.file_validate_extensions,
            };
        }

        return mappedFields;
    };

    // Main element loop that determines which input to render based on the type key
    const renderElement = (element: any, index: number): React.Node => {
        switch (element.type) {
            case "webform_markup":
                return <RenderHTML className="rte" markup={element.markup} />;
            case "textfield":
            case "tel":
            case "email":
            case "textarea":
                return (
                    <ReactFormField
                        {...mapTwigToReactField(element)}
                        fieldComponent={CustomField}
                        value={inputValues[element.name]}
                        onChange={(event: any) => {
                            setInputValues({
                                [element.name]: event.target.value,
                            });
                        }}
                    ></ReactFormField>
                );
            case "date":
                return (
                    <ReactFormField
                        {...mapTwigToReactField(element)}
                        className={"date-picker-input"}
                        fieldComponent={DatePickerField}
                        value={datePickersState[`datepicker-${element.name}`]}
                        onChange={(event: any) => {
                            setDateFieldValue(
                                `datepicker-${element.name}`,
                                event.target.value
                            );
                        }}
                    ></ReactFormField>
                );
            case "radios":
                return (
                    <RadioGroup
                        label={element.title}
                        required={element.required}
                    >
                        <div className="radio-group-col">
                            {element.options.map(
                                (option: any, index: number): React.Node => (
                                    <ReactFormField
                                        key={index}
                                        type={"radio"}
                                        name={option.name}
                                        value={option.value}
                                        label={option.label}
                                        id={option.value}
                                        fieldComponent={CustomField}
                                        hideOptional
                                        checked={
                                            inputValues[option.name] ===
                                            option.value
                                        }
                                        onChange={(event: any) => {
                                            setInputValues({
                                                [option.name]: option.value,
                                            });
                                        }}
                                    ></ReactFormField>
                                )
                            )}
                        </div>
                    </RadioGroup>
                );
            case "checkboxes":
                return (
                    <RadioGroup
                        label={element.title}
                        required={element.required}
                    >
                        <div className="radio-group-col">
                            {element.options.map(
                                (option: any, index: number): React.Node => (
                                    <ReactFormField
                                        key={index}
                                        type={"checkbox"}
                                        name={option.name}
                                        value={option.value}
                                        label={option.label}
                                        id={option.value}
                                        fieldComponent={CustomField}
                                        hideOptional
                                        checked={inputValues[option.name]}
                                        onChange={(event: any) => {
                                            setInputValues({
                                                [option.name]:
                                                    event.target.checked,
                                            });
                                        }}
                                    ></ReactFormField>
                                )
                            )}
                        </div>
                    </RadioGroup>
                );
            case "select":
                return (
                    <ReactFormField
                        {...mapTwigToReactField(element)}
                        fieldComponent={CustomSelectField}
                        value={inputValues[element.name]}
                        onCustomSelectChange={(value: string) => {
                            setInputValues({ [element.name]: value });
                        }}
                    >
                        {element.options.map(
                            (option: any, index: number): React.Node => (
                                <option value={option.value} key={index}>
                                    {option.label}
                                </option>
                            )
                        )}
                    </ReactFormField>
                );
            case "managed_file":
                return (
                    <FileUploadField
                        {...mapTwigToReactField(element)}
                        ref={fileUploaderRef}
                    />
                );
            default:
                return <p>Unkown form element type: {element.type}</p>;
        }
    };

    // Run on onSuccess & onFail in order to validate ReactForm and manually validated fields
    // Will prevent default if an error is found in either sets of fields
    const attemptFormSubmit = (
        inputs: any,
        states: any,
        event: ?SyntheticEvent<HTMLFormElement>
    ) => {
        let errors = states.reduce((prev: Array<any>, input: any): any => {
            if (input.errors && input.errors[0]) {
                return [
                    ...prev,
                    {
                        label: input.label,
                        error: input.errors[0],
                    },
                ];
            }
            return prev;
        }, []);

        // Validate file uploader field
        if (fileUploaderRef.current) {
            const result = fileUploaderRef.current.validate();
            if (result.error) {
                errors = [...errors, result];
            }
        }

        // Recaptcha
        if (recaptchaContainer.current) {
            const input = recaptchaContainer.current.querySelector(
                ".g-recaptcha-response"
            );
            if (input instanceof HTMLTextAreaElement && input.value === "") {
                errors = [
                    ...errors,
                    {
                        label: "reCAPTCHA:",
                        error: "You must fill out the reCAPTCHA",
                    },
                ];
            }
        }

        // Set form errors in state
        setFormErrors(errors);

        if (errors.length > 0) {
            // Invalid
            if (event) {
                event.preventDefault();
            }
            if (topOfFormRef.current) {
                scrollToEl(topOfFormRef.current);
            }
        } else {
            // Valid - do nothing to let the form submit as normal
        }
    };

    // Combine server errors and frontend errors into a single array
    // so they can be shown in a unified UI at the top of the form
    const serverErrorsArray = Object.keys(props.errors).map(
        (key: string): string => props.errors[key]
    );
    const frontEndErrorsArray = formErrors.map(
        ({ error, label }: any, index: number): string => {
            return `${label} ${error}`;
        }
    );
    const allErrorsArray = [...serverErrorsArray, ...frontEndErrorsArray];

    return (
        <>
            <div ref={topOfFormRef}></div>

            <ReactForm
                pageSubmit
                method="POST"
                encType="multipart/form-data"
                validators={customValidators}
                onSuccess={attemptFormSubmit}
                onError={attemptFormSubmit}
                disableErrorScroll
                additionalPropsToClean={[
                    "onCustomSelectChange",
                    "hideOptional",
                ]}
            >
                {/* Render any errors */}
                <ServerError errors={allErrorsArray}></ServerError>

                {/* Render main inputs of form */}
                {props.elements.map(
                    (element: any, index: number): React.Node => (
                        <div
                            className={`react-form__element react-form__element--${element.type}`}
                            key={index}
                        >
                            {renderElement(element, index)}
                        </div>
                    )
                )}

                {/* Hidden Inputs */}
                <RenderHTML
                    dont_replace={process.env.NODE_ENV !== "development"}
                    markup={props.form_build_id}
                />
                <RenderHTML
                    dont_replace={process.env.NODE_ENV !== "development"}
                    markup={props.form_id}
                />
                <RenderHTML
                    dont_replace={process.env.NODE_ENV !== "development"}
                    markup={props.form_token}
                />
                <div className="recaptcha-container" ref={recaptchaContainer}>
                    <RenderHTML
                        dont_replace={process.env.NODE_ENV !== "development"}
                        markup={props.captcha}
                    />
                </div>

                {/* Submit button */}
                <div className="react-form__submit-container">
                    <button
                        className="button button--blue button--inline"
                        type="submit"
                        // NOTE: IT IS IMPORTANT THAT THIS VALUE IS HERE.
                        // Drupal won't accept form submissions with files if this doesn't exist
                        value="Submit"
                        name={props.submit.name}
                    >
                        Submit Form
                    </button>
                </div>
            </ReactForm>
        </>
    );
};

export default ReactFormLoader;
